(() => {
  'use strict';

  const STORAGE_KEY = 'godshot:wishlist:v1';
  const CHANGE_EVENT = 'godshot:wishlist-change';
  const MAX_ITEMS = 100;
  const hoverDesktopQuery = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 750px)');

  let memoryHandles = [];
  let wishlistRenderToken = 0;
  let mutationSyncQueued = false;

  const normalizeHandle = (value) => {
    const handle = String(value || '').trim().toLowerCase();
    return /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle) ? handle : '';
  };

  const normalizeHandles = (values) => {
    const uniqueHandles = [];
    const seen = new Set();

    (Array.isArray(values) ? values : []).forEach((item) => {
      const handle = normalizeHandle(typeof item === 'string' ? item : item?.handle);
      if (!handle || seen.has(handle)) return;
      seen.add(handle);
      uniqueHandles.push(handle);
    });

    return uniqueHandles.slice(0, MAX_ITEMS);
  };

  const readHandles = () => {
    try {
      const storedValue = window.localStorage.getItem(STORAGE_KEY);
      if (!storedValue) return [...memoryHandles];
      const parsedValue = JSON.parse(storedValue);
      const handles = normalizeHandles(Array.isArray(parsedValue) ? parsedValue : parsedValue?.handles);
      memoryHandles = handles;
      return [...handles];
    } catch (error) {
      return [...memoryHandles];
    }
  };

  const writeHandles = (handles, detail = {}) => {
    const normalizedHandles = normalizeHandles(handles);
    memoryHandles = normalizedHandles;

    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          version: 1,
          handles: normalizedHandles,
        })
      );
    } catch (error) {
      // Storage can be unavailable in strict privacy modes. The in-memory list
      // keeps the controls usable for the current page session.
    }

    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: {
          handles: normalizedHandles,
          ...detail,
        },
      })
    );

    return normalizedHandles;
  };

  const updateHeaderCount = (handles) => {
    const count = handles.length;
    const itemLabel = count === 1 ? 'item' : 'items';

    document.querySelectorAll('[data-wishlist-count]').forEach((bubble) => {
      const displayCount = count > 99 ? '99+' : String(count);
      if (bubble.textContent !== displayCount) bubble.textContent = displayCount;
      bubble.toggleAttribute('hidden', count === 0);
    });

    document.querySelectorAll('[data-wishlist-header-link]').forEach((link) => {
      link.setAttribute('aria-label', `Wishlist, ${count} ${itemLabel}`);
    });
  };

  const updateWishlistButton = (button, savedHandles) => {
    const handle = normalizeHandle(button.dataset.wishlistHandle);
    const isSaved = Boolean(handle && savedHandles.has(handle));
    const productTitle = button.dataset.wishlistTitle || 'this product';
    const label = button.querySelector('[data-wishlist-label]');

    button.classList.toggle('is-active', isSaved);
    button.setAttribute('aria-pressed', String(isSaved));
    button.setAttribute(
      'aria-label',
      isSaved ? `Remove ${productTitle} from wishlist` : `Add ${productTitle} to wishlist`
    );

    const labelText = isSaved ? 'Remove from wishlist' : 'Add to wishlist';
    if (label && label.textContent !== labelText) label.textContent = labelText;
  };

  const syncWishlistUi = (root = document) => {
    const handles = readHandles();
    const savedHandles = new Set(handles);
    root.querySelectorAll?.('[data-wishlist-toggle]').forEach((button) =>
      updateWishlistButton(button, savedHandles)
    );
    updateHeaderCount(handles);
  };

  const setActionMenuState = (wrapper, open, returnFocus = false) => {
    if (!wrapper) return;
    const toggle = wrapper.querySelector('[data-product-card-actions-toggle]');
    const menu = wrapper.querySelector('[data-product-card-actions-menu]');
    if (!toggle || !menu) return;

    wrapper.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute(
      'aria-label',
      `${open ? 'Close' : 'Open'} actions for ${toggle.dataset.productTitle || 'this product'}`
    );

    if (!hoverDesktopQuery.matches) {
      menu.toggleAttribute('inert', !open);
      menu.setAttribute('aria-hidden', String(!open));
    } else {
      menu.removeAttribute('inert');
      menu.removeAttribute('aria-hidden');
    }

    if (!open && returnFocus) toggle.focus();
  };

  const closeActionMenus = (exceptWrapper = null, returnFocus = false) => {
    document.querySelectorAll('[data-product-card-actions].is-open').forEach((wrapper) => {
      if (wrapper !== exceptWrapper) setActionMenuState(wrapper, false, returnFocus);
    });
  };

  const prepareActionMenus = (root = document) => {
    root.querySelectorAll?.('[data-product-card-actions]').forEach((wrapper) => {
      if (wrapper.dataset.actionsReady === 'true') return;
      wrapper.dataset.actionsReady = 'true';
      setActionMenuState(wrapper, false);
    });
  };

  const syncActionMenuMode = () => {
    document.querySelectorAll('[data-product-card-actions]').forEach((wrapper) => {
      setActionMenuState(wrapper, false);
    });
  };

  const setActionStatus = (element, message) => {
    const status = element.closest('.card-wrapper')?.querySelector('[data-product-card-action-status]');
    if (status) status.textContent = message;
  };

  const toggleWishlist = (button) => {
    const handle = normalizeHandle(button.dataset.wishlistHandle);
    if (!handle) return;

    const currentHandles = readHandles();
    const isSaved = currentHandles.includes(handle);
    const productTitle = button.dataset.wishlistTitle || 'Product';

    if (!isSaved && currentHandles.length >= MAX_ITEMS) {
      setActionStatus(button, `Your wishlist can contain up to ${MAX_ITEMS} products.`);
      return;
    }

    const nextHandles = isSaved
      ? currentHandles.filter((savedHandle) => savedHandle !== handle)
      : [handle, ...currentHandles];

    setActionStatus(button, isSaved ? `${productTitle} removed from your wishlist.` : `${productTitle} saved.`);
    writeHandles(nextHandles, {
      changedHandle: handle,
      action: isSaved ? 'removed' : 'added',
    });
  };

  const fetchWishlistCard = async (handle) => {
    const localeRoot = window.Shopify?.routes?.root || '/';
    const rootWithSlash = localeRoot.endsWith('/') ? localeRoot : `${localeRoot}/`;
    const response = await fetch(`${rootWithSlash}products/${encodeURIComponent(handle)}?section_id=wishlist-card`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) throw new Error(`Unable to load ${handle} (${response.status})`);

    const parsedDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
    const productCard = parsedDocument.querySelector('[data-wishlist-card]');
    if (!productCard) throw new Error(`Wishlist card markup was not returned for ${handle}`);
    return productCard;
  };

  const fetchWishlistCards = async (handles) => {
    const results = new Array(handles.length);
    let nextIndex = 0;

    const worker = async () => {
      while (nextIndex < handles.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;

        try {
          results[currentIndex] = await fetchWishlistCard(handles[currentIndex]);
        } catch (error) {
          results[currentIndex] = null;
        }
      }
    };

    const workerCount = Math.min(4, handles.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
    return results;
  };

  const renderWishlistPage = async () => {
    const page = document.querySelector('[data-wishlist-page]');
    if (!page) return;

    const token = ++wishlistRenderToken;
    const handles = readHandles();
    const grid = page.querySelector('[data-wishlist-grid]');
    const loading = page.querySelector('[data-wishlist-loading]');
    const empty = page.querySelector('[data-wishlist-empty]');
    const clearButton = page.querySelector('[data-wishlist-clear]');
    const status = page.querySelector('[data-wishlist-page-status]');

    grid.replaceChildren();
    grid.hidden = true;
    empty.hidden = true;
    loading.hidden = false;
    clearButton.hidden = handles.length === 0;
    status.textContent = handles.length ? 'Loading your saved products…' : '';
    page.dataset.wishlistState = 'loading';

    if (handles.length === 0) {
      loading.hidden = true;
      empty.hidden = false;
      page.dataset.wishlistState = 'empty';
      return;
    }

    const cards = await fetchWishlistCards(handles);
    if (token !== wishlistRenderToken) return;

    const availableCards = cards.filter(Boolean);
    availableCards.forEach((card) => grid.appendChild(card));
    loading.hidden = true;
    grid.hidden = false;
    page.dataset.wishlistState = availableCards.length ? 'ready' : 'error';

    const failedCount = cards.length - availableCards.length;
    if (failedCount > 0) {
      status.textContent = availableCards.length
        ? `${availableCards.length} saved products shown. ${failedCount} could not be loaded.`
        : 'Your saved products could not be loaded. Please refresh the page.';
    } else {
      status.textContent = `${availableCards.length} saved ${availableCards.length === 1 ? 'product' : 'products'}.`;
    }

    prepareActionMenus(grid);
    syncWishlistUi(grid);
  };

  const scheduleMutationSync = () => {
    if (mutationSyncQueued) return;
    mutationSyncQueued = true;
    window.requestAnimationFrame(() => {
      mutationSyncQueued = false;
      prepareActionMenus(document);
      syncWishlistUi(document);
    });
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-product-card-actions-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      const wrapper = toggle.closest('[data-product-card-actions]');
      const shouldOpen = !wrapper.classList.contains('is-open');
      closeActionMenus(wrapper);
      setActionMenuState(wrapper, shouldOpen);
      return;
    }

    const wishlistButton = event.target.closest('[data-wishlist-toggle]');
    if (wishlistButton) {
      event.preventDefault();
      event.stopPropagation();
      closeActionMenus();
      toggleWishlist(wishlistButton);
      return;
    }

    const clearButton = event.target.closest('[data-wishlist-clear]');
    if (clearButton) {
      event.preventDefault();
      writeHandles([], { action: 'cleared' });
      return;
    }

    if (!event.target.closest('[data-product-card-actions]')) closeActionMenus();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openWrapper = document.querySelector('[data-product-card-actions].is-open');
    if (openWrapper) setActionMenuState(openWrapper, false, true);
  });

  document.addEventListener(CHANGE_EVENT, () => {
    syncWishlistUi(document);
    renderWishlistPage();
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    memoryHandles = [];
    syncWishlistUi(document);
    renderWishlistPage();
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    syncWishlistUi(document);
    renderWishlistPage();
  });

  hoverDesktopQuery.addEventListener?.('change', syncActionMenuMode);

  const observedRoot = document.getElementById('MainContent');
  if (observedRoot) {
    new MutationObserver(scheduleMutationSync).observe(observedRoot, {
      childList: true,
      subtree: true,
    });
  }

  window.GodshotWishlist = {
    getHandles: readHandles,
    add(handle) {
      const normalizedHandle = normalizeHandle(handle);
      if (!normalizedHandle) return readHandles();
      const handles = readHandles();
      if (!handles.includes(normalizedHandle)) handles.unshift(normalizedHandle);
      return writeHandles(handles, { changedHandle: normalizedHandle, action: 'added' });
    },
    remove(handle) {
      const normalizedHandle = normalizeHandle(handle);
      return writeHandles(
        readHandles().filter((savedHandle) => savedHandle !== normalizedHandle),
        { changedHandle: normalizedHandle, action: 'removed' }
      );
    },
    clear() {
      return writeHandles([], { action: 'cleared' });
    },
  };

  prepareActionMenus(document);
  syncWishlistUi(document);
  renderWishlistPage();
})();
